import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ConfirmDialog from "./ConfirmDialog";
import {
  fetchComputers, fetchComputerAgent, wakeComputer, shutdownComputer, setComputerOwner,
} from "../api";
import { computerState, isTransitional, isAgentDown, isJobRunning, STATE_TEXT } from "../lib/computers";
import "./ComputerMenu.css";

// Büro-Rechner starten (Besitzer, Admins) und ausschalten (Admins). Planning
// legt nur Aufträge an, ausgeführt werden sie vom Büro-Dienst auf
// relticc-server-01, der alle paar Sekunden nachfragt. Der Stand kommt per
// WebSocket-Hinweis ("planning:computers", App.js) und per Polling.

const POLL_FAST_MS = 5000;    // solange etwas startet oder herunterfährt
const POLL_SLOW_MS = 60000;
const DROPDOWN_WIDTH = 360;

const AGENT_DOWN_TEXT = "Büro-Dienst nicht erreichbar";

function PowerIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
      <line x1="12" y1="2" x2="12" y2="12" />
    </svg>
  );
}

function fmtSeen(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const date = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  const time = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  return `${date} ${time}`;
}

// Punkt am Satzende abschneiden, die Meldungen hängen selbst einen an
const noDot = (s) => (s || "").replace(/[.\s]+$/, "");

export default function ComputerMenu({ isAdmin, users, currentUserId }) {
  const [computers, setComputers] = useState([]);
  const [seenAt, setSeenAt] = useState(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState({});      // computer id → Anfrage läuft
  const [errors, setErrors] = useState({});  // computer id → Fehler der letzten Aktion
  // { kind: "normal" | "critical" | "blocked", computer, detail }
  const [confirm, setConfirm] = useState(null);
  // "blockiert"-Rückfragen, die warten, bis der offene Dialog zu ist
  const [blockedQueue, setBlockedQueue] = useState([]);
  const confirmRef = useRef(null);
  confirmRef.current = confirm;
  const wrapRef = useRef(null);
  const pillRef = useRef(null);
  const dropRef = useRef(null);
  // Eigene Ausschalt-Aufträge ohne force: kommt "blockiert" zurück, fragt der
  // zweite Dialog, ob trotzdem ausgeschaltet werden soll.
  const watchedRef = useRef(new Set());
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    // Nur die neueste Antwort zählt (WebSocket-Hinweis und Polling können sich
    // überholen); nach dem Unmount zählt keine mehr.
    const seq = ++seqRef.current;
    try {
      const [list, agent] = await Promise.all([fetchComputers(), fetchComputerAgent()]);
      if (seq !== seqRef.current) return;
      setComputers(list);
      setSeenAt(agent?.seen_at || null);
      const watched = watchedRef.current;
      if (watched.size) {
        const current = new Set();
        for (const c of list) {
          const job = c.job;
          if (!job || !watched.has(job.id)) continue;
          current.add(job.id);
          if (isJobRunning(job)) continue;
          watched.delete(job.id);
          if (job.status === "blockiert") {
            setBlockedQueue((q) => [...q, { kind: "blocked", computer: c, detail: job.detail }]);
          }
        }
        // Nicht mehr der letzte Auftrag oder älter als 10 min: vergessen
        for (const id of [...watched]) if (!current.has(id)) watched.delete(id);
      }
    } catch (e) {
      console.warn("Rechner laden fehlgeschlagen:", e);
    }
  }, []);

  useEffect(() => {
    const seq = seqRef;
    load();
    const onChanged = () => load();
    window.addEventListener("planning:computers", onChanged);
    return () => {
      window.removeEventListener("planning:computers", onChanged);
      seq.current++; // laufende Antworten verwerfen
    };
  }, [load]);

  // Nächste "Trotzdem ausschalten?"-Frage zeigen, sobald kein Dialog offen ist.
  // Ist gerade ein anderer offen (etwa Ausschalten eines zweiten Rechners),
  // wartet sie, statt verloren zu gehen.
  useEffect(() => {
    if (confirm || blockedQueue.length === 0) return;
    setConfirm(blockedQueue[0]);
    setBlockedQueue((q) => q.slice(1));
  }, [confirm, blockedQueue]);

  // Hängt auch an der Uhr (Übergang nach "ok" endet nach ein paar Minuten).
  // Währenddessen lädt das schnelle Polling alle 5 s neu und rechnet mit.
  const states = useMemo(() => {
    const m = {};
    for (const c of computers) m[c.id] = computerState(c);
    return m;
  }, [computers]);
  const fast = computers.some((c) => isTransitional(states[c.id]));

  useEffect(() => {
    const t = setInterval(load, fast ? POLL_FAST_MS : POLL_SLOW_MS);
    return () => clearInterval(t);
  }, [fast, load]);

  // Dropdown: position:fixed unter dem Knopf, rechtsbündig, nie über den
  // Bildschirmrand. Wie CustomSelect: beim Scrollen der Seite schließen (die
  // Position wird nur beim Öffnen berechnet), Klick außerhalb und Escape auch.
  const place = useCallback(() => {
    const r = pillRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = window.innerWidth;
    const width = Math.min(DROPDOWN_WIDTH, vw - 24);
    const right = Math.min(Math.max(12, vw - r.right), vw - 12 - width);
    setPos({ top: r.bottom + 6, right, width });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const onDown = (e) => {
      if (confirmRef.current) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onScroll = (e) => {
      if (dropRef.current && e.target instanceof Node && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    // Escape schließt erst den Bestätigungsdialog (der ruft preventDefault),
    // erst das nächste das Dropdown
    const onKey = (e) => {
      if (e.key === "Escape" && !e.defaultPrevented && !confirmRef.current) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  const setFlag = (setter, id, value) => setter((prev) => {
    const next = { ...prev };
    if (value) next[id] = value; else delete next[id];
    return next;
  });

  // Antwort des Servers sofort zeigen, nicht erst nach dem nächsten Laden
  const applyJob = (id, job) => setComputers((prev) => prev.map((c) => (c.id === id ? { ...c, job } : c)));

  const runAction = async (c, action) => {
    setFlag(setErrors, c.id, null);
    setFlag(setBusy, c.id, true);
    try {
      await action();
    } catch (e) {
      setFlag(setErrors, c.id, e.message || "Fehler");
    } finally {
      setFlag(setBusy, c.id, false);
      load();
    }
  };

  const wake = (c) => runAction(c, async () => applyJob(c.id, await wakeComputer(c.id)));

  const shutdown = (c, force) => runAction(c, async () => {
    const job = await shutdownComputer(c.id, force);
    if (!force) watchedRef.current.add(job.id);
    applyJob(c.id, job);
  });

  const changeOwner = (c, value) => runAction(c, async () => {
    const updated = await setComputerOwner(c.id, value === "" ? null : Number(value));
    setComputers((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...updated } : x)));
  });

  const askShutdown = (c) => setConfirm({ kind: c.critical ? "critical" : "normal", computer: c });

  const confirmShutdown = async () => {
    const { kind, computer } = confirm;
    try {
      // Kritisch: der Dienst würde ohne force blockieren, die Warnung hier
      // ist die ausdrückliche Bestätigung. Blockiert: zweite Bestätigung.
      await shutdown(computer, kind !== "normal");
    } finally {
      setConfirm(null);
    }
  };

  const agentDown = isAgentDown(seenAt);

  if (computers.length === 0) return null;

  const confirmDialog = confirm && (
    <ConfirmDialog
      message={
        confirm.kind === "critical"
          ? `${confirm.computer.name} ist der Polarise-Produktivrechner. Laufende Kundensitzungen brechen ab. Wirklich ausschalten?`
          : confirm.kind === "blocked"
            ? `${confirm.computer.name} ist in Benutzung: ${noDot(confirm.detail)}. Trotzdem ausschalten?`
            : `${confirm.computer.name} ausschalten? Windows fährt nach 60 Sekunden herunter. Wer davor sitzt, sieht eine Meldung und kann abbrechen.`
      }
      confirmLabel={confirm.kind === "blocked" ? "Trotzdem ausschalten" : "Ausschalten"}
      onConfirm={confirmShutdown}
      onCancel={() => setConfirm(null)}
    />
  );

  // Fehler der eigenen Aktion (z. B. 429) oder Ergebnis "fehler" vom Dienst
  const errorOf = (c) => errors[c.id] || (c.job?.status === "fehler" ? c.job.detail || "Fehler" : null);

  // --- Mitarbeiter mit genau einem Rechner: ein Knopf ---
  if (!isAdmin && computers.length === 1) {
    const c = computers[0];
    const state = states[c.id];
    const err = errorOf(c);
    const canWake = state === "off" && c.can_wake && !busy[c.id];
    const title = [
      state === "off" ? `${c.name} starten` : STATE_TEXT[state],
      agentDown && AGENT_DOWN_TEXT,
      err && `Fehler: ${err}`,
    ].filter(Boolean).join(" · ");
    return (
      <div className="computer-menu" ref={wrapRef}>
        <button
          type="button"
          ref={pillRef}
          className={`computer-pill is-${state}${canWake ? " is-clickable" : ""}${err ? " has-error" : ""}`}
          onClick={() => { if (canWake) wake(c); }}
          title={title}
          aria-label={`${c.name}: ${STATE_TEXT[state]}`}
        >
          <PowerIcon />
          <span className={`computer-dot is-${state}`} />
          <span className="computer-pill-status">
            <span className="computer-pill-label">{c.name} · </span>{STATE_TEXT[state]}
          </span>
        </button>
      </div>
    );
  }

  // --- Admin oder mehrere Rechner: Dropdown ---
  const onlineCount = computers.filter((c) => c.online).length;
  // Nicht archivierte Nutzer; ein archivierter Besitzer bleibt wählbar, damit
  // das Select seinen Wert zeigt.
  const ownerOptions = (c) => {
    const list = (users || []).filter((u) => !u.archived);
    if (c.owner_user_id != null && !list.some((u) => u.id === c.owner_user_id)) {
      return [...list, { id: c.owner_user_id, username: c.owner_name || `#${c.owner_user_id}` }];
    }
    return list;
  };

  return (
    <div className="computer-menu" ref={wrapRef}>
      <button
        type="button"
        ref={pillRef}
        className={`computer-pill is-clickable${open ? " is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title={agentDown ? AGENT_DOWN_TEXT : "Rechner"}
        aria-label={`Rechner: ${onlineCount} von ${computers.length} an`}
        aria-expanded={open}
      >
        <PowerIcon />
        <span><span className="computer-pill-label">Rechner </span>{onlineCount}/{computers.length}</span>
        <svg className="computer-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && pos && (
        <div
          className="computer-dropdown"
          ref={dropRef}
          style={{ top: pos.top, right: pos.right, width: pos.width }}
        >
          {agentDown && (
            <div className="computer-agent-hint">
              {AGENT_DOWN_TEXT}{seenAt ? ` · zuletzt ${fmtSeen(seenAt)}` : ""}
            </div>
          )}
          {computers.map((c) => {
            const state = states[c.id];
            const err = errorOf(c);
            const blocked = c.job?.status === "blockiert" ? c.job.detail : null;
            return (
              <div key={c.id} className={`computer-row${c.owner_user_id === currentUserId ? " is-own" : ""}`}>
                <span className={`computer-dot is-${state}`} />
                <div className="computer-row-main">
                  <div className="computer-row-name">
                    <strong>{c.name}</strong>
                    {c.label && <span className="computer-row-label">{c.label}</span>}
                  </div>
                  {isAdmin ? (
                    <select
                      className="computer-owner-select"
                      value={c.owner_user_id ?? ""}
                      onChange={(e) => changeOwner(c, e.target.value)}
                      disabled={!!busy[c.id]}
                      aria-label={`Besitzer von ${c.name}`}
                    >
                      <option value="">– niemand –</option>
                      {ownerOptions(c).map((u) => (
                        <option key={u.id} value={u.id}>{u.username}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="computer-row-owner">{c.owner_name || "–"}</div>
                  )}
                  {err && <div className="computer-row-error">{err}</div>}
                  {!err && blocked && <div className="computer-row-note">Nicht ausgeschaltet: {blocked}</div>}
                </div>
                <span className={`computer-row-status is-${state}`}>{STATE_TEXT[state]}</span>
                <div className="computer-row-action">
                  {state === "off" && c.can_wake && (
                    <button type="button" className="computer-action-btn" onClick={() => wake(c)} disabled={!!busy[c.id]}>
                      Starten
                    </button>
                  )}
                  {state === "on" && c.can_shutdown && (
                    <button type="button" className="computer-action-btn is-danger" onClick={() => askShutdown(c)} disabled={!!busy[c.id]}>
                      Ausschalten
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
